import { Avatar, AvatarImage, AvatarFallback } from '@radix-ui/react-avatar'
import { useAuth } from '@/lib/auth.context'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Button } from '@/components/ui/button'
import { LogOut, ChevronDown, Moon, Sun  } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useTheme } from '../../lib/theme-context'

function ThemeToggle() {
  const { theme, setTheme } = useTheme()

  return (
    <Button variant="outline" className="hover:bg-transparent" size="icon" onClick={() => setTheme(theme === 'light' ? 'dark' : 'light')}>
      <Sun className="h-5 w-5 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0" />
      <Moon className="absolute h-5 w-5 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
      <span className="sr-only">Toggle theme</span>
    </Button>
  )
}

export function Header() {
  const { user, status, logout } = useAuth()
  const { t } = useTranslation()

  const handleLogout = async () => {
    await logout(true)
  }

  if (status === 'loading') return <div className="h-16" />

  if (!user) {
    return null
  }

  const getInitials = (name: string) => {
    const nameParts = name.split(' ').filter(Boolean)
    if (nameParts.length === 0) return '??'
    if (nameParts.length === 1) return nameParts[0].substring(0, 2).toUpperCase()

    const firstInitial = nameParts[0].charAt(0)
    const lastInitial = nameParts[nameParts.length - 1].charAt(0)
    return `${firstInitial}${lastInitial}`.toUpperCase()
  }

  const firstName = user.user_metadata?.first_name
  const lastName = user.user_metadata?.last_name

  const fullName = [firstName, lastName].filter(Boolean).join(' ') || 'Partner User'
  const initials = getInitials(fullName)
  return (
    <header className="flex items-center justify-end bg-transparent px-8 h-16 gap-4">
      <ThemeToggle />
      <DropdownMenu>
        <DropdownMenuTrigger asChild className="focus:outline-none focus-visible:outline-none focus:ring-0 focus-visible:ring-0 [&[data-state=open]]:outline-none">
          <Button variant="ghost" className="flex items-center gap-3 px-3 py-2 h-auto hover:bg-lavender-50 rounded-lg cursor-pointer transition-colors focus:outline-none focus-visible:outline-none focus:ring-0 focus-visible:ring-0 data-[state=open]:outline-none">
            <Avatar>
              <AvatarImage src={undefined} className="w-8 h-8 rounded-full" />
              <AvatarFallback className="flex items-center justify-center w-8 h-8 rounded-full bg-lavender-200 text-lavender-700 font-bold">
                {initials}
              </AvatarFallback>
            </Avatar>
            <span className="text-lavender-900 font-medium">{fullName}</span>
            <ChevronDown className="w-4 h-4 text-lavender-600" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={handleLogout}>
            <LogOut className="mr-2 h-4 w-4" />
            <span>{t('logout')}</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  )
}
